import { useEffect, useRef, type PropsWithChildren, type ReactNode } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import './modal.css';

interface ModalProps extends PropsWithChildren {
  /** Rendered inside the heading, so a node must still read as the title. */
  title: ReactNode;
  onClose: () => void;
  labelledBy?: string;
  className?: string;
}

export function Modal({ title, onClose, labelledBy = 'modal-title', className = '', children }: ModalProps) {
  const { t } = useTranslation();
  const onCloseRef = useRef(onClose);

  onCloseRef.current = onClose;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div
      className="modal__backdrop"
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
      onWheel={(event) => { if (event.target === event.currentTarget) event.preventDefault(); }}
      onTouchMove={(event) => { if (event.target === event.currentTarget) event.preventDefault(); }}
    >
      <section className={`modal ${className}`.trim()} role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
        <header className="modal__header">
          <h2 id={labelledBy}>{title}</h2>
          <button className="modal__close" type="button" onClick={onClose} aria-label={t('common.buttons.close')}>×</button>
        </header>
        {children}
      </section>
    </div>
  );
}
